import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import {
  CALCULATION_LABEL_LIMIT,
  CALCULATION_UNIT_LIMIT,
  CALCULATOR_EXPRESSION_LIMIT,
  formatCalculationNoteLine,
  formatCalculationNumber,
  labeledCalculation,
  toggleLastNumberSign,
} from '@brunel/shared';

/**
 * #729 — compact calculator that sits under the active note. Arithmetic is the
 * shared deterministic engine; this component is only the Command Web adapter.
 * State lives in DeveloperNotepad so it survives page switches while Notes stays
 * mounted.
 */
export interface NoteCalculatorState {
  readonly expression: string;
  readonly label: string;
  readonly unit: string;
}

export const EMPTY_NOTE_CALCULATOR: NoteCalculatorState = { expression: '', label: '', unit: '' };

type Key = { readonly label: string; readonly aria: string; readonly apply: (expression: string) => string; readonly className?: string };

const append = (text: string) => (expression: string) => expression + text;

const KEYS: readonly Key[] = [
  { label: 'C', aria: 'Clear', apply: () => '', className: 'calc-fn' },
  { label: '⌫', aria: 'Backspace', apply: (expression) => [...expression].slice(0, -1).join(''), className: 'calc-fn' },
  { label: '(', aria: 'Open parenthesis', apply: append('('), className: 'calc-fn' },
  { label: ')', aria: 'Close parenthesis', apply: append(')'), className: 'calc-fn' },
  { label: '÷', aria: 'Divide', apply: append('÷'), className: 'calc-op' },
  { label: '7', aria: '7', apply: append('7') },
  { label: '8', aria: '8', apply: append('8') },
  { label: '9', aria: '9', apply: append('9') },
  { label: '%', aria: 'Percent', apply: append('%'), className: 'calc-fn' },
  { label: '×', aria: 'Multiply', apply: append('×'), className: 'calc-op' },
  { label: '4', aria: '4', apply: append('4') },
  { label: '5', aria: '5', apply: append('5') },
  { label: '6', aria: '6', apply: append('6') },
  { label: '±', aria: 'Change sign', apply: toggleLastNumberSign, className: 'calc-fn' },
  { label: '−', aria: 'Subtract', apply: append('−'), className: 'calc-op' },
  { label: '1', aria: '1', apply: append('1') },
  { label: '2', aria: '2', apply: append('2') },
  { label: '3', aria: '3', apply: append('3') },
  { label: '.', aria: 'Decimal point', apply: append('.') },
  { label: '+', aria: 'Add', apply: append('+'), className: 'calc-op' },
];

/**
 * Physical keyboard / numeric keypad → calculator key (by aria name). Active
 * while focus is anywhere in the calculator except its text fields, which keep
 * normal typing. The note editor and the rest of the page are never captured.
 */
const KEYBOARD: Readonly<Record<string, string>> = {
  ...Object.fromEntries([...'0123456789'].map((digit) => [digit, digit])),
  '+': 'Add',
  '-': 'Subtract',
  '*': 'Multiply',
  x: 'Multiply',
  X: 'Multiply',
  '/': 'Divide',
  '%': 'Percent',
  '(': 'Open parenthesis',
  ')': 'Close parenthesis',
  '.': 'Decimal point',
  ',': 'Decimal point',
  '=': 'Equals',
  Enter: 'Equals',
  Backspace: 'Backspace',
  Escape: 'Clear',
  Delete: 'Clear',
  F9: 'Change sign',
};

const PRESS_FLASH_MS = 120;

export function NoteCalculator({
  state, onChange, onAdd, targetTitle, onCreatePage, creating = false, status = null,
}: {
  readonly state: NoteCalculatorState;
  readonly onChange: (next: NoteCalculatorState) => void;
  /** Receives the formatted note line. Only called when a note page is active. */
  readonly onAdd: (line: string) => void;
  /** Title of the page Add writes to, or null when there is no active page. */
  readonly targetTitle: string | null;
  readonly onCreatePage?: () => void;
  readonly creating?: boolean;
  readonly status?: string | null;
}) {
  const result = labeledCalculation(state);
  const hasExpression = state.expression.trim().length > 0;
  const line = result.ok ? formatCalculationNoteLine(result.calculation) : null;
  const canAdd = line !== null && targetTitle !== null;

  const setExpression = (expression: string) =>
    onChange({ ...state, expression: expression.slice(0, CALCULATOR_EXPRESSION_LIMIT) });
  const equals = () => {
    if (result.ok) setExpression(formatCalculationNumber(result.calculation.result));
  };
  const add = () => {
    if (canAdd && line !== null) onAdd(line);
  };

  const section = useRef<HTMLElement | null>(null);
  const [pressed, setPressed] = useState<string | null>(null);
  useEffect(() => {
    if (pressed === null) return;
    const timer = setTimeout(() => setPressed(null), PRESS_FLASH_MS);
    return () => clearTimeout(timer);
  }, [pressed]);

  const press = (aria: string) => {
    if (aria === 'Equals') equals();
    else if (aria === '0') setExpression(`${state.expression}0`);
    else {
      const key = KEYS.find((row) => row.aria === aria);
      if (key) setExpression(key.apply(state.expression));
    }
  };

  // Cmd/Ctrl+Enter adds to the note from anywhere in the calculator.
  const onCalculatorKey = (event: KeyboardEvent<HTMLElement>) => {
    if (event.nativeEvent.isComposing) return;
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      add();
      return;
    }
    if (event.target instanceof HTMLInputElement) return;
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    const aria = KEYBOARD[event.key];
    if (aria === undefined) return;
    event.preventDefault();
    setPressed(aria);
    press(aria);
  };

  // Clicking the calculator (not a text field) gives it keyboard focus, also in
  // browsers such as Safari that do not focus buttons on click.
  const focusCalculator = (target: EventTarget) => {
    if (target instanceof HTMLInputElement) return;
    section.current?.focus({ preventScroll: true });
  };

  const onExpressionKey = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing || event.metaKey || event.ctrlKey) return;
    if (event.key === 'Enter' || event.key === '=') {
      event.preventDefault();
      equals();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setExpression('');
    }
  };
  const onDetailKey = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing || event.key !== 'Enter' || event.metaKey || event.ctrlKey) return;
    event.preventDefault();
    add();
  };

  return (
    <section
      ref={section}
      className="note-calculator"
      aria-label="Calculator"
      data-testid="note-calculator"
      tabIndex={-1}
      onKeyDown={onCalculatorKey}
      onPointerDown={(event) => focusCalculator(event.target)}
    >
      <div className="calc-display">
        <input
          className="calc-expression"
          aria-label="Calculation"
          inputMode="decimal"
          autoComplete="off"
          spellCheck={false}
          placeholder="e.g. 1250 × 4"
          value={state.expression}
          maxLength={CALCULATOR_EXPRESSION_LIMIT}
          onChange={(event) => setExpression(event.target.value)}
          onKeyDown={onExpressionKey}
        />
        <output className={result.ok || !hasExpression ? 'calc-result' : 'calc-result calc-invalid'} aria-live="polite">
          {result.ok
            ? `= ${formatCalculationNumber(result.calculation.result)}${result.calculation.unit ? ` ${result.calculation.unit}` : ''}`
            : hasExpression ? result.error : '= 0'}
        </output>
      </div>

      <div className="calc-keys" role="group" aria-label="Calculator keys">
        {KEYS.map((key) => (
          <button
            type="button"
            key={key.aria}
            className={[key.className, pressed === key.aria ? 'calc-pressed' : null].filter(Boolean).join(' ') || undefined}
            aria-label={key.aria}
            onClick={() => press(key.aria)}
          >
            {key.label}
          </button>
        ))}
        <button type="button" className={pressed === '0' ? 'calc-zero calc-pressed' : 'calc-zero'} aria-label="0" onClick={() => press('0')}>0</button>
        <button type="button" className={pressed === 'Equals' ? 'calc-equals calc-pressed' : 'calc-equals'} aria-label="Equals" disabled={!result.ok} onClick={equals}>=</button>
      </div>

      <div className="calc-details">
        <input
          className="calc-label"
          aria-label="Label: what is this?"
          placeholder="What is this? (optional)"
          value={state.label}
          maxLength={CALCULATION_LABEL_LIMIT}
          onChange={(event) => onChange({ ...state, label: event.target.value })}
          onKeyDown={onDetailKey}
        />
        <input
          className="calc-unit"
          aria-label="Unit"
          placeholder="Unit"
          value={state.unit}
          maxLength={CALCULATION_UNIT_LIMIT}
          onChange={(event) => onChange({ ...state, unit: event.target.value })}
          onKeyDown={onDetailKey}
        />
      </div>

      {line !== null && <p className="calc-preview" title="Line added to the note">{line}</p>}

      <div className="calc-actions">
        <button
          type="button"
          className="calc-add"
          disabled={!canAdd}
          title={targetTitle === null ? 'Create or select a note page first' : `Add to “${targetTitle}”`}
          onClick={add}
        >
          Add calculation to notes
        </button>
        {targetTitle === null && (
          <span className="calc-no-page">
            Add needs a note page.
            {onCreatePage && (
              <button type="button" className="ghost" disabled={creating} onClick={onCreatePage}>
                {creating ? 'Creating…' : '+ New page'}
              </button>
            )}
          </span>
        )}
        {status !== null && <span className="calc-status" role="status">{status}</span>}
      </div>
      <p className="calc-hint">
        Click the calculator, then type: 0–9 + − × ÷ ( ) % · Enter = · Backspace · Esc clears · ⌘/Ctrl+Enter adds to notes
      </p>
    </section>
  );
}
